import type { FieldValues, Message, Path, UseControllerProps } from 'react-hook-form';
import { OptionWithHint } from '#components/patterns/forms/select/option/OptionWithHint.tsx';
import type { Item } from '#components/patterns/forms/select/select-async-searchable/SelectAsyncSearchable.tsx';
import { SelectAsyncSearchableControlled } from '#components/patterns/forms/select/select-async-searchable/SelectAsyncSearchableControlled.tsx';
import { SelectItem } from '#components/patterns/forms/select/select-item/SelectItem.tsx';
import { makeAllRolesQueryOptions } from './SelectRole.queries.ts';

type Props<T extends FieldValues, N extends Path<T>> = {
  errorMessage?: Message;
  label: React.ReactNode;
  className?: string;
  filterItems?: (role: Item) => boolean;
  renderOptions?: React.ComponentProps<typeof SelectAsyncSearchableControlled>['renderOptions'];
} & UseControllerProps<T, N> &
  Omit<
    React.ComponentProps<typeof SelectAsyncSearchableControlled>,
    'getItemsQueryOptions' | 'control' | 'renderOptions'
  >;

export function SelectRole<T extends FieldValues, N extends Path<T>>({
  label,
  errorMessage,
  control,
  name,
  className,
  filterItems,
  ...props
}: Props<T, N>) {
  return (
    <SelectAsyncSearchableControlled
      className={className}
      getItemsQueryOptions={makeAllRolesQueryOptions(filterItems)}
      control={control}
      name={name}
      label={label}
      placeholder="Select a role…"
      optionsName="roles"
      errorMessage={errorMessage}
      renderOptions={({ onChange, items }) => (
        <>
          {items.map((item) => (
            <SelectItem value={item.value} onClick={() => onChange(item.value)} searchable={true} key={item.value}>
              {item.hint ?
                <OptionWithHint value={item.label} hint={item.hint} />
              : item.label}
            </SelectItem>
          ))}
        </>
      )}
      {...props}
    />
  );
}
